/**
 * Release 3 EXP-1 (lane C) — "Custom export…" dialog: build an `ImageTransform`, preview the output name, then
 * `downloadVersion` with the inline fields; 200 saves the cached rendition at once, 202 tracks the `export_render` job.
 */
import { useState } from 'react';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { useExportVersion } from '@/lib/api/queries';
import { isApiError } from '@/lib/api/errors';
import { toast } from '@/stores/ui';
import { TransformFields } from './TransformFields';
import { DEFAULT_FORM, exportFilename, transformFromForm, type TransformForm } from './exportModel';
import { completeExport } from './runExport';

export function ExportDialog({ versionId, filename, onClose }: { versionId: string; filename: string; onClose: () => void }) {
  const [form, setForm] = useState<TransformForm>(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);
  const exportM = useExportVersion();
  const parsed = transformFromForm(form);
  const preview = parsed.ok ? exportFilename(filename, parsed.transform) : null;
  const submit = () => {
    const p = transformFromForm(form);
    if (!p.ok) return setError(p.error);
    setError(null);
    const name = exportFilename(filename, p.transform);
    exportM.mutate(
      { versionId, transform: p.transform },
      {
        onSuccess: (r) => {
          onClose();
          void completeExport(r, { filename: name });
        },
        onError: (e) => setError(isApiError(e) ? (e.code === 'transform_unsupported' ? 'Only images can be exported with a transform' : e.message) : 'Export failed'),
      },
    );
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Custom export"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={exportM.isPending} data-export-submit>
            {exportM.isPending ? 'Requesting…' : 'Export'}
          </Button>
        </>
      }
    >
      <div className="space-y-3" data-export-dialog>
        <TransformFields value={form} onChange={(v) => (setForm(v), setError(null))} />
        <p className="text-xs text-fg-muted" data-export-preview>
          {preview ? (
            <>
              Output: <span className="font-mono">{preview}</span>
            </>
          ) : (
            (parsed.ok ? null : parsed.error)
          )}
        </p>
        {error && (
          <p role="alert" className="text-sm text-danger" data-export-error>
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** Shown when a preset export is requested on a non-image (the API would answer 422). */
export function notAnImageToast(): void {
  toast({ kind: 'warning', title: 'Only images can be exported with a preset', body: 'Other files download as their original.' });
}
